import React, {useState, useEffect, useRef} from 'react';
import cx from 'classnames';
import {useRouter} from 'next/router';
import {useTranslations, createTranslator} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {SectionPaddingMdToZero} from '@/section';
import Container from '@/container';
import Link from '@/links';
import {withNeutral700} from '@/hocs/withColors';
import {withBoldWeight, withItalic} from '@/hocs/withFontType';
import {H2, P} from '@/typography';
import {ListTerms} from '@/ui/lists/list-icons';
import {withMarginBottomMd, withMarginBottomXxl} from '@/hocs/withSpacing';
import withBreakpoints from '@/hocs/withBreakpoints';
import {withVisibleDesktop993, withHiddenDesktop993} from '@/hocs/withResponsiveUtils';
import Breadcrumbs from '@/ui/breadcrumbs';
import Dropdown from '@/form-fields/dropdown';
import Tabs from '@/ui/tabs/tabs';
import LegalHero from '@/content/legal/hero';
import usePrivacyNoticeTabItems from '@/ui/tabs/tab-items/privacy-notice-tabs';
import legal from 'pages/legal/legal.module.scss';
import {pageMessagesProps} from 'helpers/i18n';
import {
  scrollToTopOfPage,
  handleDropdown,
  scrollableDropdownMob
} from '../components/ui/legal/legal.helper';
import shared from '../messages/en/shared.json';
import legalMessages from '../messages/en/legal.json';
import candidateTerms from '../messages/en/candidateTerms.json';

const seoMetas = props => {
  const seoT = createTranslator({locale: props.locale, messages: props.messages});
  return {
    title: seoT('candidateTerms.seo.title'),
    description: seoT('candidateTerms.seo.description'),
    canonical: seoT('candidateTerms.seo.canonical')
  };
};

const List = props => <ListTerms {...props} />;
const BoldP = withBoldWeight(P);
const GreyCopy = withNeutral700(withItalic('span'));
const MarginedH2 = withMarginBottomMd(H2);
const BreadcrumbsDesktop = withVisibleDesktop993(withMarginBottomXxl('div'));
const HiddenInDesktop = withHiddenDesktop993('div');

const RecruitmentTemplatePage = () => {
  const router = useRouter();
  const dropdownRef = useRef();
  const [isDropdownFixed, setIsDropdownFixed] = useState(false);
  const t = useTranslations('candidateTerms');
  const legalT = useTranslations('legal');
  const tabItems = usePrivacyNoticeTabItems();

  const ResponsiveMenu = withBreakpoints({
    desktop: () => (
      <Tabs
        activeTab="recruitment-privacy-template"
        title={legalT('sectionTitles.privacyNotice')}
        tabItems={tabItems}
        router={router}
      />
    ),
    mobile: () => (
      <div
        ref={dropdownRef}
        className={cx(isDropdownFixed ? legal['legal-dropdown-fixed'] : legal['legal-dropdown'])}>
        <Dropdown
          name="privacy-notice"
          defaultValue="recruitment-privacy-template"
          variant="green800"
          values={tabItems}
          onChange={item => handleDropdown(item, router)}
        />
      </div>
    )
  });

  useEffect(() => {
    scrollableDropdownMob(dropdownRef, setIsDropdownFixed);
  }, []);

  const RecruitmentTemplateContent = () => (
    <>
      <MarginedH2>{t('heading')}</MarginedH2>
      <P>
        {t('intro1.before')}
        <GreyCopy>{t('intro1.grey')}</GreyCopy>
        {t('intro1.after')}
      </P>
      <P>
        {t('controller.before')}
        <GreyCopy>{t('controller.grey')}</GreyCopy>
        {t('controller.after')}
      </P>
      <P>
        {t('representative.before')}
        <GreyCopy>{t('representative.grey')}</GreyCopy>
        {t('representative.after')}
      </P>
      <P>
        {t('representativeDetail.before')}
        <GreyCopy>{t('representativeDetail.grey')}</GreyCopy>
        {t('representativeDetail.after')}
      </P>
      <P>{t('usingWorkable')}</P>
      <P>{t('applyingForRole')}</P>
      <P>{t('partnerNotice')}</P>
      <H2 className="heading-4">{t('sections.personalInformation.title')}</H2>
      <BoldP className="heading-5">{t('sections.personalInformation.collectFromYouTitle')}</BoldP>
      <P>{t('sections.personalInformation.collectFromYouIntro')}</P>
      <List>
        {t.raw('sections.personalInformation.collectFromYouItems').map(item => (
          <li key={item}>{item}</li>
        ))}
        <li>
          {t('sections.personalInformation.collectFromYouOtherItem.before')}
          <GreyCopy>{t('sections.personalInformation.collectFromYouOtherItem.grey')}</GreyCopy>
          {t('sections.personalInformation.collectFromYouOtherItem.after')}
        </li>
      </List>

      <BoldP className="heading-5">
        {t('sections.personalInformation.collectFromOtherSourcesTitle')}
      </BoldP>
      {t.raw('sections.personalInformation.collectFromOtherSourcesParagraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}

      <H2 className="heading-4">{t('sections.usesOfInformation.title')}</H2>
      <BoldP className="heading-5">{t('sections.usesOfInformation.lawfulBasisTitle')}</BoldP>

      <P>{t('sections.usesOfInformation.lawfulBasisParagraph')}</P>

      <BoldP className="heading-5">{t('sections.usesOfInformation.purposesTitle')}</BoldP>

      <P>{t('sections.usesOfInformation.purposesIntro')}</P>
      <List>
        {t.raw('sections.usesOfInformation.purposesItems').map(item => (
          <li key={item}>{item}</li>
        ))}
        <li>
          {t('sections.usesOfInformation.purposesOtherItem.before')}
          <GreyCopy>{t('sections.usesOfInformation.purposesOtherItem.grey')}</GreyCopy>
          {t('sections.usesOfInformation.purposesOtherItem.after')}
        </li>
      </List>

      <BoldP className="heading-5">{t('sections.usesOfInformation.automatedDecisionTitle')}</BoldP>

      <P>{t('sections.usesOfInformation.automatedDecisionParagraph')}</P>

      <H2 className="heading-4">{t('sections.disclosure.title')}</H2>
      {t.raw('sections.disclosure.paragraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}

      <H2 className="heading-4">{t('sections.storage.title')}</H2>
      <BoldP className="heading-5">{t('sections.storage.securityTitle')}</BoldP>
      {t.raw('sections.storage.securityParagraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}

      <BoldP className="heading-5">{t('sections.storage.whereWeStoreTitle')}</BoldP>
      <P>
        {t('sections.storage.whereWeStore.before')}
        <GreyCopy>{t('sections.storage.whereWeStore.grey')}</GreyCopy>
        {t('sections.storage.whereWeStore.after')}
      </P>
      {t.raw('sections.storage.transferParagraphs').map(paragraph => (
        <P key={paragraph}>{paragraph}</P>
      ))}

      <BoldP className="heading-5">{t('sections.storage.howLongTitle')}</BoldP>
      <P>
        {t('sections.storage.howLong.before')}
        <GreyCopy>{t('sections.storage.howLong.grey')}</GreyCopy>
        {t('sections.storage.howLong.after')}
      </P>
      <P>
        <GreyCopy>{t('sections.storage.deletionIntro')}</GreyCopy>
      </P>
      <List>
        {t.raw('sections.storage.deletionItems').map(item => (
          <li key={item}>
            <GreyCopy>{item}</GreyCopy>
          </li>
        ))}
      </List>

      <H2 className="heading-4">{t('sections.yourRights.title')}</H2>
      <P>{t('sections.yourRights.intro')}</P>
      <List>
        {t.raw('sections.yourRights.items').map(item => (
          <li key={item}>{item}</li>
        ))}
      </List>
      <P>
        {t.rich('sections.yourRights.icoParagraph', {
          a: chunks => (
            <Link
              href="https://ico.org.uk/for-organisations/guide-to-the-general-data-protection-regulation-gdpr/individual-rights/"
              target="_blank"
              variant="default">
              {chunks}
            </Link>
          )
        })}
      </P>
      <P>{t('sections.yourRights.exerciseIntro')}</P>
      <List>
        {t.raw('sections.yourRights.exerciseItems').map(item => (
          <li key={item}>{item}</li>
        ))}
      </List>

      <H2 className="heading-4">{t('sections.complain.title')}</H2>
      <P>{t('sections.complain.paragraph')}</P>
      <P>
        {t.rich('sections.complain.gdprComplaint', {
          a: chunks => (
            <Link
              href="https://eur-lex.europa.eu/legal-content/EN/TXT/PDF/?uri=CELEX:32016R0679&from=EN"
              target="_blank"
              variant="default">
              {chunks}
            </Link>
          )
        })}
      </P>
      <H2 className="heading-4">{t('sections.contact.title')}</H2>
      <P>
        {t('sections.contact.message.before')}
        <GreyCopy>{t('sections.contact.message.grey')}</GreyCopy>
        {t('sections.contact.message.after')}
      </P>
    </>
  );

  return (
    <>
      <LegalHero title={legalT('sectionTitles.privacyNotice')} />
      <SectionPaddingMdToZero>
        <Container>
          <Grid fluid>
            <BreadcrumbsDesktop>
              <Breadcrumbs
                clickableLink={{text: legalT('breadcrumb.legal'), urlCopy: '/legal'}}
                title={legalT('sectionTitles.privacyNotice')}
                activeTab={
                  tabItems.find(item => item.value === 'recruitment-privacy-template').text
                }
              />
            </BreadcrumbsDesktop>
            <Row>
              <Col xs={12} md={12} lg={3}>
                <ResponsiveMenu />
              </Col>
              <Col xs={12} md={12} lgOffset={1} lg={8}>
                <RecruitmentTemplateContent />
              </Col>
            </Row>
          </Grid>
          <HiddenInDesktop>
            <Link variant="go-to-top-arrow" onClick={scrollToTopOfPage} />
          </HiddenInDesktop>
        </Container>
      </SectionPaddingMdToZero>
    </>
  );
};

export default withMainLayout(RecruitmentTemplatePage, seoMetas);

export const messagesForIntl = {shared, legal: legalMessages, candidateTerms};

export function getStaticProps() {
  return pageMessagesProps(messagesForIntl);
}
